// src/crm/ugc/UgcSettingsPanel.tsx
//
// Section « Programme UGC » des réglages (spec § 6.6) : adresse des factures (affichée aux
// créateurs qui facturent), adresse de l'e-mail du matin (préremplie avec l'adresse du compte
// admin tant qu'elle n'est pas réglée) et coordonnées d'OTIA LTD (détail de facture, pages légales).

import { useEffect, useId, useState, type FormEvent } from 'react'
import { DEFAULT_ALERT_EMAIL, type UgcSettings } from '../../lib/ugcTypes'
import { useSaveUgcSettings, useUgcSettings } from './hooks'
import './ugc.css'

type Draft = { invoiceEmail: string; alertEmail: string; companyDetails: string }

function toDraft(s: UgcSettings): Draft {
  return {
    invoiceEmail: s.invoiceEmail ?? '',
    alertEmail: s.alertEmail ?? DEFAULT_ALERT_EMAIL,
    companyDetails: s.companyDetails ?? '',
  }
}

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim())

export function UgcSettingsPanel() {
  const settings = useUgcSettings()
  const save = useSaveUgcSettings()
  const ids = { invoice: useId(), alert: useId(), company: useId() }
  const [draft, setDraft] = useState<Draft | null>(null)

  useEffect(() => {
    if (settings.data && draft === null) setDraft(toDraft(settings.data))
  }, [settings.data, draft])

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!draft) return
    save.mutate({
      invoiceEmail: orNull(draft.invoiceEmail),
      alertEmail: orNull(draft.alertEmail),
      companyDetails: orNull(draft.companyDetails),
    })
  }

  return (
    <section className="ugc-card ugc-settings" aria-labelledby={`${ids.invoice}-title`}>
      <h2 id={`${ids.invoice}-title`} className="ugc-card-title">
        Programme UGC
      </h2>
      {settings.isPending && <p className="ugc-muted">Chargement…</p>}
      {settings.isError && <p className="ugc-error">Les réglages n'ont pas pu être chargés : {settings.error.message}</p>}
      {draft && (
        <form className="ugc-inline-form" onSubmit={submit}>
          <label htmlFor={ids.invoice}>Adresse des factures</label>
          <input
            id={ids.invoice}
            className="ugc-input"
            type="email"
            value={draft.invoiceEmail}
            onChange={(e) => setDraft({ ...draft, invoiceEmail: e.target.value })}
          />
          <p className="ugc-muted">Affichée aux créateurs et créatrices qui facturent, dans leur espace et leurs e-mails (prévue : factures@basaltlab.app).</p>

          <label htmlFor={ids.alert}>Adresse de l'e-mail du matin</label>
          <input
            id={ids.alert}
            className="ugc-input"
            type="email"
            value={draft.alertEmail}
            onChange={(e) => setDraft({ ...draft, alertEmail: e.target.value })}
          />
          <p className="ugc-muted">Un e-mail part chaque matin s'il y a quelque chose à faire dans le programme.</p>

          <label htmlFor={ids.company}>Coordonnées d'OTIA LTD</label>
          <textarea
            id={ids.company}
            className="ugc-textarea"
            rows={4}
            maxLength={1000}
            value={draft.companyDetails}
            onChange={(e) => setDraft({ ...draft, companyDetails: e.target.value })}
          />
          <p className="ugc-muted">Reprises dans le détail de facture et les pages légales du portail.</p>

          <div className="ugc-actions">
            <button type="submit" className="ugc-btn ugc-btn--go" disabled={save.isPending}>
              {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
          {save.isSuccess && (
            <p role="status" className="ugc-notice">
              Réglages enregistrés.
            </p>
          )}
          {save.error && (
            <p role="alert" className="ugc-error">
              {save.error.message}
            </p>
          )}
        </form>
      )}
    </section>
  )
}
